import { useMemo } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowLongRightIcon, CheckIcon } from '@heroicons/react/16/solid';
import {
  Container,
  Eyebrow,
  SectionHead,
} from '@/components/layout-primitives';
import { BOOKING_URL, CONTACT_URL, buttonBase } from '@/lib/bw';
import { SITE_URL } from '@/lib/site';
import { SERVICES_ENABLED, getService } from '@/lib/services';
import { useDocument } from '@/lib/use-document';
import { NOT_FOUND_ROUTE, serviceRoute } from '@/lib/seo';
import { NotFound } from '@/routes/not-found';

/**
 * Service landing pages, one per entry in lib/services.ts.
 *
 * A single dynamic route rather than one near-identical file per service: the
 * pages differ only in copy, and hand-maintained templates would drift apart
 * within a month. prerenderRoutes() in lib/seo.ts expands them all at build
 * time, so they are static HTML in production -- what generateStaticParams
 * used to do.
 *
 * Band rhythm follows the home page's rule -- no two neighbouring sections
 * share a surface, and each seam carries exactly one 2px rule:
 *   hero cream / includes white / prose ink-050 / proof ink / FAQ white /
 *   CTA yellow, then the shared ink footer.
 */

export function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const service = SERVICES_ENABLED && slug ? getService(slug) : undefined;

  /* Memoised because serviceRoute() builds a fresh object each call, and
     useDocument keys its effect on identity -- without this it would rewrite
     the whole head on every render. Hooks run unconditionally, so this sits
     above the guard; on a miss it falls back to the 404 metadata, which is
     what NotFound applies anyway. */
  const meta = useMemo(
    () => (service ? serviceRoute(service.slug) : null) ?? NOT_FOUND_ROUTE,
    [service],
  );
  useDocument(meta);

  if (!service) return <NotFound />;

  const related = service.related
    .map((s) => getService(s))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  /* Service and FAQPage schema. The FAQ answers here are the same text the
     page renders -- structured data that disagrees with the visible page is
     worse than none at all. */
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        name: service.name,
        description: service.metaDescription,
        serviceType: service.name,
        url: `${SITE_URL}/services/${service.slug}`,
        provider: {
          '@type': 'ProfessionalService',
          name: 'Butter Websites',
          url: SITE_URL,
          email: 'taylor@butterwebsites.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Cincinnati',
            addressRegion: 'OH',
            addressCountry: 'US',
          },
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: service.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    ],
  };

  return (
    <main className="bg-white">
      <script
        type="application/ld+json"
        // Content is our own static copy, not user input.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* --------------------------------------------------------------- Hero */}
      <section className="bg-bw-cream py-16 md:py-24">
        <Container>
          <div className="flex flex-col items-center gap-8 text-center">
            {/* The service name is the h1 -- it is what the page is about,
                and it is the phrase people search for. The display line under
                it is a tagline, so it renders as a paragraph rather than as a
                heading with no section beneath it.

                `name` rather than a separate eyebrow string, so the h1 matches
                the nav, the footer and the <title> exactly. The eyebrow is
                uppercased in CSS, which hid that those strings had drifted
                into three sentence-case and two title-case variants. */}
            <SectionHead
              eyebrowAs="h1"
              as="p"
              eyebrow={service.name}
              title={service.h1}
              lead={service.lead}
              align="center"
            />
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${buttonBase} group w-full bg-bw-yellow text-bw-ink sm:w-auto`}
              >
                Contact us
                <ArrowLongRightIcon
                  aria-hidden="true"
                  className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1"
                />
              </a>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${buttonBase} w-full bg-white text-bw-ink sm:w-auto`}
              >
                Book a call
              </a>
            </div>
          </div>
        </Container>
      </section>

      {/* ----------------------------------------------------- What's included */}
      <section className="border-t-2 border-bw-ink py-16 md:py-24">
        <Container>
          <SectionHead eyebrow="What's included" title="What you get" />
          <ul className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {service.includes.map((item) => (
              <li
                key={item.title}
                className="flex flex-col gap-3 rounded-[20px] border-2 border-bw-ink bg-white p-6 shadow-sticker"
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-bw-ink bg-bw-yellow">
                  <CheckIcon aria-hidden="true" className="h-4 w-4" />
                </span>
                <h3 className="font-display text-[19px] font-bold leading-[1.25] text-bw-ink">
                  {item.title}
                </h3>
                <p className="text-[15px] leading-[1.6] text-ink-700">
                  {item.body}
                </p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {/* -------------------------------------------------------------- Detail */}
      <section className="border-y-2 border-bw-ink bg-ink-050 py-16 md:py-24">
        <Container>
          <div className="mx-auto flex max-w-[760px] flex-col gap-10">
            {service.sections.map((block) => (
              <div key={block.heading} className="flex flex-col gap-3">
                <h2 className="bw-display text-[27px] leading-[1.15] text-bw-ink md:text-[34px]">
                  {block.heading}
                </h2>
                <p className="text-[17px] leading-[1.6] text-ink-700">
                  {block.body}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* --------------------------------------------------------------- Proof */}
      <section className="bg-bw-ink py-16 text-white md:py-24">
        <Container>
          <div className="mx-auto flex max-w-[760px] flex-col items-center gap-6 text-center">
            <p className="bw-eyebrow text-ink-300">Proof</p>
            <blockquote className="text-[21px] leading-[1.5] md:text-[27px]">
              Lulu&apos;s Sweets Boutique, an independent bakery in West
              Chester, Ohio, has done over{' '}
              <span className="bw-display text-bw-yellow">$500,000</span> in
              direct online sales since publishing the site we built for them.
            </blockquote>
            <Link
              to="/#work"
              className="bw-eyebrow rounded-[4px] text-bw-yellow underline decoration-dotted underline-offset-4 hover:text-white focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-bw-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-bw-ink"
            >
              See the numbers
            </Link>
          </div>
        </Container>
      </section>

      {/* ----------------------------------------------------------------- FAQ */}
      <section className="py-16 md:py-24">
        <Container>
          <SectionHead eyebrow="FAQ" title="Common questions" />
          <dl className="mx-auto mt-10 flex w-full max-w-[860px] flex-col gap-4">
            {service.faqs.map((faq) => (
              <div
                key={faq.question}
                className="rounded-[20px] border-2 border-bw-ink bg-white p-6 shadow-sticker"
              >
                <dt className="font-display text-[17px] font-bold text-bw-ink">
                  {faq.question}
                </dt>
                <dd className="mt-2 text-[17px] leading-[1.6] text-ink-700">
                  {faq.answer}
                </dd>
              </div>
            ))}
          </dl>

          {related.length > 0 ? (
            <div className="mx-auto mt-12 flex w-full max-w-[860px] flex-col items-center gap-4 text-center">
              <Eyebrow>Also worth a look</Eyebrow>
              <div className="flex flex-wrap justify-center gap-3">
                {related.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/services/${item.slug}`}
                    className="rounded-full border-2 border-bw-ink bg-white px-5 py-2.5 font-display text-[15px] font-bold text-bw-ink shadow-sticker-sm transition-colors hover:bg-yellow-soft focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-bw-ink focus-visible:ring-offset-2"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          ) : null}
        </Container>
      </section>

      {/* ----------------------------------------------------------------- CTA */}
      {/* Top rule only: the footer draws its own top border, and two 2px rules
          meeting would render as a single 4px line. */}
      <section className="border-t-2 border-bw-ink bg-bw-yellow py-16 md:py-24">
        <Container>
          <div className="flex flex-col items-center gap-8 lg:flex-row lg:justify-between">
            <div className="flex flex-col items-center gap-4 text-center lg:items-start lg:text-left">
              <p className="bw-eyebrow text-ink-700">Contact us</p>
              <h2 className="bw-display text-[34px] leading-[1.1] text-bw-ink md:text-[44px]">
                Get a <i>butter</i> website
              </h2>
              <p className="max-w-[520px] text-[19px] leading-[1.6] text-ink-700">
                Tell us what you sell and what is getting in the way. We&apos;ll
                tell you what we&apos;d do about it.
              </p>
            </div>

            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${buttonBase} w-full bg-bw-ink text-white sm:w-auto`}
              >
                Contact us
              </a>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${buttonBase} w-full bg-white text-bw-ink sm:w-auto`}
              >
                Book a call
              </a>
            </div>
          </div>
        </Container>
      </section>
    </main>
  );
}
